// Copyright (c) StrangeBee 2024

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Link, Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { ADMIN_ORG_ID, HeaderActionType } from '@Types';
import THEntityLayout from '@Organisms/THEntityLayout/THEntityLayout';
import OrgLayoutSider from '@Organisms/Organisation/OrgLayoutSider/OrgLayoutSider';
import OrgLayoutTitle from '@Organisms/Organisation/OrgLayoutTitle/OrgLayoutTitle';
import { useCurrentUser } from '@Queries/userQueries';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import ChildRoutes from '@Pages/Admin/Organisations/Details/routes';
import { ICONS } from '@Constants/iconsApp';
import LoadedContent from '@Atoms/LoadedContent/LoadedContent';
import useOneQuery from '@Hooks/useOneQuery';
import client from '@Client';
import { OrganisationOutput, PermissionsEnum, AdminPermissionsEnum } from 'thehive-sdk';
import { computeEventList } from '@Organisms/Organisation/eventList';
import { findDefaultPath } from '@Helpers/RedirectPath';
import { PatternType } from '@Constants/patternsRoute';
import { useGetAccessibleRoutes } from '@Helpers/getAccessibleRoutes';
import { computeGetSelector } from '@Organisms/Organisation/selector';
import useUserPermissions from '@Hooks/useUserPermissions';

const OrganisationsDetails = ({ scope }: { scope: string }): ReactElement | null => {
    const i = useLocalMessage();
    const { orgId } = useParams<{ orgId: string }>();
    const accessibleRoutes = useGetAccessibleRoutes(ChildRoutes);
    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_ORGANISATION] });

    const history = useHistory();
    const pathRoot = scope == 'admin' ? 'administration' : 'organisation';
    const pathRouteBack = `${pathRoot}.organizations` as PatternType;

    const {
        data: data,
        isFetching,
        error,
        refetch: dataRefetch,
    } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId }),
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.OrganisationController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    dataRefetch();
                },
                onError: handleNotificationError,
            }),
        [dataRefetch, i],
    );

    const { data: currentUserData } = useCurrentUser();

    const { name = '' } = data || {};

    const HeaderActions: HeaderActionType[] = [];

    if (!currentUserData) {
        return null;
    }

    const { organisation, organisations = [] } = currentUserData;

    const userOrgs = organisations?.map(({ organisation }) => organisation) || [];

    const isUserAdmin = organisation === ADMIN_ORG_ID;

    if (!isUserAdmin && !userOrgs.includes(orgId)) {
        openMessage('error', i('notification.organisation.unauthorized'));
        history.push(RouteBuilder.compute(pathRouteBack));
    }

    if (error) {
        openMessage('error', i('notification.organisation.doesnotExist'));
        return <Redirect to={RouteBuilder.compute(pathRouteBack)} />;
    }

    if (!isFetching && !data) {
        return <Redirect to={RouteBuilder.compute(pathRouteBack)} />;
    }

    if (isFetching || !data) {
        return <LoadedContent />;
    }

    return (
        <THEntityLayout
            className="layout-org"
            routes={accessibleRoutes}
            leftSiderContent={
                <OrgLayoutSider data={data} eventList={eventList} orgId={orgId} hasPermissions={hasPermissions} />
            }
            title={<OrgLayoutTitle data={data} />}
            actions={HeaderActions}
            entityId={name}
            paramName="orgId"
        />
    );
};

const MenuLink = ({ scope }: { scope: string }): ReactElement => {
    const local = useLocalMessage();
    const [organisation, setOrganisation] = useState('');
    const { data: currentUser } = useCurrentUser();

    const pathRoot = scope == 'admin' ? 'administration' : 'organisation';

    const { permissions = [] } = currentUser || {};

    const defaultPath = findDefaultPath(permissions, {
        [PermissionsEnum.MANAGE_USER]: `${pathRoot}.users`,
        [PermissionsEnum.MANAGE_ORGANISATION]: `${pathRoot}.links`,
        [PermissionsEnum.MANAGE_CONFIG]: `${pathRoot}.settings`,
        [PermissionsEnum.MANAGE_TAG]: `${pathRoot}.customTags`,
        [PermissionsEnum.MANAGE_KNOWLEDGE_BASE]: `organisation.attachments`,
        [PermissionsEnum.MANAGE_FUNCTION_CREATE]: `${pathRoot}.functions`,
        [PermissionsEnum.MANAGE_FUNCTION_INVOKE]: `${pathRoot}.functions`,
    });

    useEffect(() => {
        if (currentUser) {
            setOrganisation(currentUser.organisation);
        }
    }, [currentUser]);

    return (
        <Link to={RouteBuilder.compute(defaultPath, { orgId: organisation })}>
            {local('mainLayout.adminMenu.organisation')}
        </Link>
    );
};

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch('*/organisations/:orgId');

    const { orgId = '' } = (match?.params as any) || {};

    return orgId;
};

const pathAdmin = RouteBuilder.compute('administration.layout');
const [selectTabAdmin] = pathAdmin.split(':');

export const Route: RouteType = {
    path: pathAdmin,
    menu: {
        label: 'organisation.layout.nav-bar.users',
        key: 'organisation',
        icon: <ICONS.OrganisationIcon />,
        render: <MenuLink scope="admin" />,
        selectTab: selectTabAdmin,
        group: 'config',
        space: ['user'],
    },
    content: <OrganisationsDetails scope="admin" />,
    space: ['admin'],
    permissionNeeded: [
        PermissionsEnum.MANAGE_ORGANISATION,
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_USER],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_CONFIG],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_CASE_TEMPLATE],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_TAG],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_KNOWLEDGE_BASE],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_REPORT_TEMPLATE],

        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_FUNCTION_CREATE],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_FUNCTION_INVOKE],
    ],
    shared: true,
    breadcrumb: {
        label: <BreadcrumbLabel />,
        isLink: false,
    },
};

const pathOrg = RouteBuilder.compute('organisation.layout');
const [selectTabOrg] = pathOrg.split(':');

export const RouteUser: RouteType = {
    path: pathOrg,
    menu: {
        label: 'organisation.layout.nav-bar.users',
        key: 'organisation',
        icon: <ICONS.OrganisationIcon />,
        render: <MenuLink scope="user" />,
        selectTab: selectTabOrg,
        group: 'config',
        space: ['user'],
    },
    content: <OrganisationsDetails scope="user" />,
    space: ['user'],
    permissionNeeded: [
        PermissionsEnum.MANAGE_USER,
        PermissionsEnum.MANAGE_CONFIG,
        PermissionsEnum.MANAGE_CASE_TEMPLATE,
        PermissionsEnum.MANAGE_KNOWLEDGE_BASE,
        PermissionsEnum.MANAGE_TAG,
        PermissionsEnum.MANAGE_REPORT_TEMPLATE,
        PermissionsEnum.MANAGE_FUNCTION_CREATE,
        PermissionsEnum.MANAGE_FUNCTION_INVOKE,
    ],
    shared: true,
    breadcrumb: {
        label: <BreadcrumbLabel />,
        isLink: false,
    },
};
